/** @jsx jsx */
import React, { useCallback, useState, type PropsWithChildren } from 'react';
import { jsx } from '@compiled/react';
import { Stack, Inline, Pressable, xcss, Text } from '@atlaskit/primitives';
import DropdownMenu, {
	DropdownItemGroup,
	DropdownItem,
	type CustomTriggerProps,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ReplyLeftIcon from '@atlaskit/icon-lab/core/reply-left';
import EditIcon from '@atlaskit/icon/core/edit';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import CopyIcon from '@atlaskit/icon/core/copy';
import AkToolTip from '@atlaskit/tooltip';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';

import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { isJsmLoomExperimentEnabled } from '@atlassian/jira-loom-jsm-agent-response/src/common/utils.tsx';
import draftMessages from '@atlassian/jira-issue-view-common/src/messages/drafts.tsx';
import { ModalDropdownItemTrigger } from '@atlassian/entry-point-modal-trigger/ModalDropdownItemTrigger';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { deleteIssueLinkConfirmationEntryPoint } from '../confirmation-modal/entrypoint.tsx';
import messages from '../messages.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';
import CommentReactions from './CommentReactions.tsx';
import { useDelete, useEdit, useHideSave, useIssueCreate, useReply } from './utils.tsx';
import ReplyAction from './ReplyAction.tsx';
import EditAction from './EditAction.tsx';
import DeleteAction from './DeleteAction.tsx';
import HideAction from './HideAction.tsx';
import RetryAction from './RetryAction.tsx';
import ErrorEditAction from './ErrorEditAction.tsx';
import CancelAction from './CancelAction.tsx';
import PinAction from './actions/PinAction.tsx';
import ScrollToCommentAction from './actions/ScrollToCommentAction.tsx';

const deleteConfirmModalProps = {
	width: 'small',
	testId: 'issue-comment-base.ui.comment.confirmation-modal.modal-dialog',
};

const CommentActionsModern = () => {
	const [isCommentMoreActionMenuOpen, setIsCommentMoreActionMenuOpen] = useState(false);
	const [tooltipMessage, setTooltipMessage] = useState(messages.copyLink);
	const context = useCommentContext();
	const { formatMessage } = useIntl();
	const onEditClick = useEdit();
	const onHideSave = useHideSave();
	const onDeleteClick = useDelete();
	const onReplyClick = useReply();
	const onIssueCreate = useIssueCreate();

	const handleDropdownClick = useCallback(
		(eventName: string) => {
			fireUIAnalytics(
				context.createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' }),
				eventName,
			);
			if (!isCommentMoreActionMenuOpen) {
				setIsCommentMoreActionMenuOpen(true);
			} else {
				setIsCommentMoreActionMenuOpen(false);
			}
		},
		[context, isCommentMoreActionMenuOpen],
	);

	const onClickCreateIssueButton = () => {
		if (!context.onClickCreateIssue) {
			return;
		}

		context.onClickCreateIssue({
			onIssueCreate,
		});
	};

	const handleCopy = useCallback(async () => {
		try {
			await globalThis.navigator.clipboard.writeText(
				`${context.fullIssueUrl}?focusedCommentId=${context.commentId}`,
			);
			setTooltipMessage(messages.linkCopied);
			setTimeout(() => {
				setTooltipMessage(messages.copyLink);
				setIsCommentMoreActionMenuOpen(false);
			}, 800);
			fireUIAnalytics(
				context.createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' }),
				'meatballMenuCopyLinkButton',
			);
		} catch (e) {
			// TODO: fire error analytics
			// show error flag.
		}
	}, [context]);
	const handleOpenChange = useCallback(({ event }: OnOpenChangeArgs) => {
		// workaround as we don't have callback when clicking outside the dropdown menu
		if (event?.target instanceof HTMLElement && event.target.closest?.('[role="menu"]') === null) {
			setIsCommentMoreActionMenuOpen(false);
		}
	}, []);

	if (context.isDeleted || context.isOptimistic || context.isEditing) {
		return [];
	}
	return (
		<Stack
			key="actions"
			testId={`issue-comment-base.ui.comment.custom-comment.${context.commentId}.comment-actions`}
		>
			<Inline space="space.100" alignBlock="start" shouldWrap>
				{context.canAdd && context.isThreadedCommentsEnabled && (
					<Inline key="reply">
						<AkToolTip content={formatMessage(messages.reply)}>
							<Pressable
								onClick={() => onReplyClick()}
								xcss={commentActionButtonStyles}
								testId="issue-comment-base.ui.comment.custom-comment.reply-tooltip"
							>
								<ReplyLeftIcon label={formatMessage(messages.reply)} />
							</Pressable>
						</AkToolTip>
					</Inline>
				)}
				{context.showReactions &&
				expVal('thor_surface_reaction_comment_experiment', 'isSurfaceReactionsEnabled', false) ? (
					<CommentReactions />
				) : undefined}
				<Inline>
					{context.canEdit &&
						(isJsmLoomExperimentEnabled().enabled ? (
							<TenantContextSubscriber>
								{({ tenantContext: { isSiteAdmin } }) => {
									return (
										<AkToolTip
											content={
												context.isThirdPartySyncOn && fg('jira_issue_view_show_slack_sync_info')
													? formatMessage(messages.disabledEditTooltip)
													: formatMessage(messages.edit)
											}
										>
											<Pressable
												key="edit"
												xcss={commentActionButtonStyles}
												onClick={(...args) => onEditClick(...args, isSiteAdmin)}
												// eslint-disable-next-line jira/integration/test-id-by-folder-structure
												testId="issue-comment-base.ui.comment.edit-tooltip"
												isDisabled={
													fg('jira_issue_view_show_slack_sync_info')
														? context.isThirdPartySyncOn
														: undefined
												}
											>
												<EditIcon label={formatMessage(messages.edit)} />
											</Pressable>
											{context.hasDraft ? ` (${formatMessage(draftMessages.draftIndicator)})` : ''}
										</AkToolTip>
									);
								}}
							</TenantContextSubscriber>
						) : (
							<AkToolTip
								content={
									context.isThirdPartySyncOn && fg('jira_issue_view_show_slack_sync_info')
										? formatMessage(messages.disabledEditTooltip)
										: formatMessage(messages.edit)
								}
							>
								<Pressable
									key="edit"
									onClick={onEditClick}
									xcss={commentActionButtonStyles}
									testId="issue-comment-base.ui.comment.custom-comment.edit-tooltip"
									isDisabled={
										fg('jira_issue_view_show_slack_sync_info')
											? context.isThirdPartySyncOn
											: undefined
									}
								>
									<EditIcon label={formatMessage(messages.edit)} />
									{context.hasDraft ? ` (${formatMessage(draftMessages.draftIndicator)})` : ''}
								</Pressable>
							</AkToolTip>
						))}
				</Inline>
				<Inline>
					<DropdownMenu
						trigger={({ triggerRef, ...props }: CustomTriggerProps) => {
							return (
								<AkToolTip content={formatMessage(messages.moreActions)}>
									<Pressable
										{...props}
										ref={triggerRef}
										onClick={() => {
											handleDropdownClick('meatballMenuMoreActionsButton');
										}}
										xcss={commentActionButtonStyles}
										testId="issue-comment-base.ui.comment.custom-comment.more-actions-tooltip"
									>
										<ShowMoreHorizontalIcon label={formatMessage(messages.moreActions)} />
									</Pressable>
								</AkToolTip>
							);
						}}
						shouldRenderToParent
						isOpen={isCommentMoreActionMenuOpen}
						onOpenChange={handleOpenChange}
					>
						<DropdownItemGroup>
							<AkToolTip
								content={() => {
									return formatMessage(tooltipMessage);
								}}
							>
								{(tooltipProps) => (
									<DropdownItem {...tooltipProps} onClick={handleCopy}>
										<Inline alignBlock="center" space="space.200">
											<Text>{formatMessage(messages.copyLink)}</Text>
											<CopyIcon label={formatMessage(messages.copyLink)} />
										</Inline>
									</DropdownItem>
								)}
							</AkToolTip>

							{context.canAdd &&
								context.isThreadedCommentsEnabled &&
								context.canCreateWorkItemInComment &&
								expVal(
									'work_item_create_from_comment_and_pin_comment',
									'hasWorkItemCreate',
									false,
								) && (
									<DropdownItem onClick={() => onClickCreateIssueButton()}>
										<FormattedMessage {...messages.createWorkItem} />
									</DropdownItem>
								)}

							{context.canPin &&
								expVal('work_item_create_from_comment_and_pin_comment', 'hasPinComment', false) && (
									<PinAction handleDropdownClick={handleDropdownClick} />
								)}
							{context.canScrollToOriginalCommentFromPinned &&
								expVal('work_item_create_from_comment_and_pin_comment', 'hasPinComment', false) && (
									<ScrollToCommentAction handleDropdownClick={handleDropdownClick} />
								)}

							{context.canDelete &&
								(context.isThirdPartySyncOn && fg('jira_issue_view_show_slack_sync_info') ? (
									<AkToolTip content={formatMessage(messages.disabledDeleteTooltip)}>
										<DropdownItem key="delete" isDisabled>
											{formatMessage(messages.delete)}
										</DropdownItem>
									</AkToolTip>
								) : (
									<ModalDropdownItemTrigger
										entryPoint={deleteIssueLinkConfirmationEntryPoint}
										entryPointProps={{
											commentId: context.commentId,
											eventOccurredAt: context.eventOccurredAt,
											jsdIncidentActivityViewHidden: context.jsdIncidentActivityViewHidden,
											hasReplies: context.hasReplies,
											parentId: context.parentId,
											isRealTimeCommentUpdated: context.isRealTimeCommentUpdated,
										}}
										modalProps={deleteConfirmModalProps}
										onClick={() => {
											handleDropdownClick('meatballMenuDeleteCommentButton');
											fg('add-analytics-for-delete-comment-ken-1993') ? onDeleteClick() : undefined;
										}}
									>
										<FormattedMessage {...messages.delete} />
									</ModalDropdownItemTrigger>
								))}
							{context.canEdit && context.isInternal && context.isIssueOfIncidentsPractice && (
								<DropdownItem key="hide" onClick={onHideSave}>
									{formatMessage(
										context.jsdIncidentActivityViewHidden ? messages.unhide : messages.hide,
									)}
								</DropdownItem>
							)}
						</DropdownItemGroup>
					</DropdownMenu>
				</Inline>
			</Inline>
		</Stack>
	);
};

const CommentActionsLegacy = () => {
	const context = useCommentContext();
	if (context.isDeleted || context.isOptimistic || context.isEditing) {
		return null;
	}

	return (
		<ActionsLayout>
			{expVal('thor_surface_reaction_comment_experiment', 'isSurfaceReactionsEnabled', false)
				? context.showReactions && <CommentReactions key="reactions" />
				: undefined}
			{context.canAdd && context.isThreadedCommentsEnabled && <ReplyAction key="reply" />}
			{context.canEdit && !context.hasFloatingMenu && <EditAction key="edit" />}
			{context.canDelete && !context.hasFloatingMenu && <DeleteAction key="delete" />}
			{context.canEdit && context.isInternal && context.isIssueOfIncidentsPractice && (
				<HideAction key="hide" />
			)}
			{expVal('thor_surface_reaction_comment_experiment', 'isSurfaceReactionsEnabled', false)
				? undefined
				: context.showReactions && <CommentReactions key="reactions" />}
		</ActionsLayout>
	);
};

const ActionsError = () => (
	<ActionsLayout>
		<RetryAction key="retry" />
		<ErrorEditAction key="edit" />
		<CancelAction key="cancel" />
	</ActionsLayout>
);

const ActionsLayout = ({ children }: PropsWithChildren) => {
	const { commentId } = useCommentContext();

	return (
		<Inline
			space="space.100"
			testId={`issue-comment-base.ui.comment.custom-comment.${commentId}.comment-actions`}
			alignBlock="center"
		>
			{children}
		</Inline>
	);
};
const ActionsComponent = componentWithCondition(
	() => expVal('jira_work_item_comment_action_modernisation', 'isEnabled', false),
	CommentActionsModern,
	CommentActionsLegacy,
);

const CommentActions = () => {
	const { isError, isOptimistic, hasSaveFailed } = useCommentContext();

	if (isOptimistic && !hasSaveFailed) {
		return null;
	}

	if (isError) {
		return <ActionsError />;
	}

	return <ActionsComponent />;
};
export default CommentActions;

const commentActionButtonStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: '24px',
	position: 'relative',
	background: 'none',
	borderRadius: 'border.radius',
	marginBottom: 'space.050',
	paddingBlockStart: 'space.025',
	paddingBlockEnd: 'space.025',
	paddingInlineStart: 'space.050',
	paddingInlineEnd: 'space.050',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});
